import { addDays } from 'date-fns';
import React, { useCallback, useRef } from 'react';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { Timestamp } from 'shared/types/timestamp';

const noop = () => {};

export enum MovementModes {
  Move = 'move',
  ResizeStart = 'resize-start',
  ResizeEnd = 'resize-end',
}

type DragState = {
  isDragging: boolean;
  mode: MovementModes | null;
  startX: number;
  initialLeft: number;
  initialWidth: number;
};

export type Options = {
  goal: Goal;
  itemContainerRef: React.RefObject<HTMLDivElement>;
  dayWidth: number;
  onDragEnd?: (id: ID, startDate: Timestamp, deadline: Timestamp) => void;
  onDragStart?: (mode: MovementModes) => void;
  onPreviewDatesChange?: (startDate?: Date, endDate?: Date) => void;
  onMouseMove?: () => void;
};

export const useDragHandlers = ({
  goal,
  itemContainerRef,
  dayWidth,
  onDragEnd = noop,
  onDragStart,
  onPreviewDatesChange,
  onMouseMove,
}: Options) => {
  const dragState = useRef<DragState>({
    isDragging: false,
    mode: null,
    startX: 0,
    initialLeft: 0,
    initialWidth: 0,
  }).current;

  const hasDragged = useRef(false);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragState.isDragging || !itemContainerRef.current) {
        return;
      }
      e.preventDefault();
      const dx = e.clientX - dragState.startX;
      if (!hasDragged.current && Math.abs(dx) > 5) {
        hasDragged.current = true;
        if (dragState.mode) {
          onDragStart?.(dragState.mode);
        }
        onMouseMove?.();
      }
      const bar = itemContainerRef.current;
      let previewStartDate = goal.startDate
        ? new Date(goal.startDate)
        : undefined;
      let previewEndDate = goal.deadline ? new Date(goal.deadline) : undefined;
      const dayDelta = Math.round(dx / dayWidth);
      const snappedDx = dayDelta * dayWidth;

      if (dragState.mode === 'move') {
        bar.style.left = `${dragState.initialLeft + snappedDx}px`;
        if (goal.startDate && goal.deadline) {
          previewStartDate = addDays(new Date(goal.startDate), dayDelta);
          previewEndDate = addDays(new Date(goal.deadline), dayDelta);
        }
      } else if (dragState.mode === 'resize-end') {
        bar.style.width = `${Math.max(dayWidth, dragState.initialWidth + snappedDx)}px`;
        if (goal.deadline) {
          previewEndDate = addDays(new Date(goal.deadline), dayDelta);
        }
      } else if (dragState.mode === 'resize-start') {
        const newWidth = Math.max(dayWidth, dragState.initialWidth - snappedDx);
        if (newWidth > dayWidth || snappedDx < 0) {
          bar.style.width = `${newWidth}px`;
          bar.style.left = `${dragState.initialLeft + snappedDx}px`;
        }
        if (goal.startDate) {
          previewStartDate = addDays(new Date(goal.startDate), dayDelta);
        }
      }
      // Clamp if start > end
      if (
        previewStartDate &&
        previewEndDate &&
        previewStartDate > previewEndDate
      ) {
        if (dragState.mode === 'resize-start') {
          previewStartDate = previewEndDate;
        } else {
          previewEndDate = previewStartDate;
        }
      }
      if (onPreviewDatesChange && previewStartDate && previewEndDate) {
        onPreviewDatesChange(previewStartDate, previewEndDate);
      }
    },
    [
      dragState.isDragging,
      dragState.startX,
      dragState.mode,
      dragState.initialLeft,
      dragState.initialWidth,
      itemContainerRef,
      goal.startDate,
      goal.deadline,
      dayWidth,
      onPreviewDatesChange,
      onMouseMove,
      onDragStart,
    ],
  );

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      if (!dragState.isDragging || !goal.startDate || !goal.deadline) {
        return;
      }
      e.preventDefault();
      const dx = e.clientX - dragState.startX;
      const dayDelta = Math.round(dx / dayWidth);
      let newStartDate = goal.startDate;
      let newEndDate = goal.deadline;

      if (dragState.mode === 'move') {
        newStartDate = addDays(new Date(goal.startDate), dayDelta);
        newEndDate = addDays(new Date(goal.deadline), dayDelta);
      } else if (dragState.mode === 'resize-end') {
        newEndDate = addDays(new Date(goal.deadline), dayDelta);
      } else if (dragState.mode === 'resize-start') {
        newStartDate = addDays(new Date(goal.startDate), dayDelta);
      }

      if (new Date(newStartDate) > new Date(newEndDate)) {
        if (dragState.mode === 'resize-start') {
          newStartDate = newEndDate;
        } else {
          newEndDate = newStartDate;
        }
      }

      onDragEnd(goal.id, newStartDate, newEndDate);
      dragState.isDragging = false;
      dragState.mode = null;

      if (itemContainerRef.current) {
        itemContainerRef.current.style.zIndex = '10';
      }

      document.body.style.cursor = 'default';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (onPreviewDatesChange) {
        onPreviewDatesChange(undefined, undefined);
      }
      setTimeout(() => {
        hasDragged.current = false;
      }, 0);
    },
    [
      dragState,
      goal.startDate,
      goal.deadline,
      goal.id,
      dayWidth,
      onDragEnd,
      itemContainerRef,
      handleMouseMove,
      onPreviewDatesChange,
    ],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, mode: MovementModes) => {
      if (e.button !== 0) {
        return;
      }
      e.stopPropagation();
      dragState.isDragging = true;
      dragState.mode = mode;
      dragState.startX = e.clientX;

      if (itemContainerRef.current) {
        dragState.initialLeft = itemContainerRef.current.offsetLeft;
        dragState.initialWidth = itemContainerRef.current.offsetWidth;
        itemContainerRef.current.style.zIndex = '20';
      }

      document.body.style.cursor = mode.includes('resize')
        ? 'ew-resize'
        : 'grabbing';
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [dragState, handleMouseMove, handleMouseUp, itemContainerRef],
  );

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  };
};
